import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Pencil, Trash2, Upload } from "lucide-react";
import { API, authHeaders, clearToken, getToken } from "@/lib/api";

const fileUrl = (u) => (u && u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const inputClass =
  "w-full rounded-none border border-seam bg-pit px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-acid";

export default function GalleryManager() {
  const navigate = useNavigate();
  const [pairs, setPairs] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [label, setLabel] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/gallery`, { headers: authHeaders() });
      setPairs(data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        clearToken();
        navigate("/admin/login");
      } else {
        setError("Could not load gallery pairs. Try refreshing.");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!label.trim() || !beforeFile || !afterFile) {
      setError("A label and both photos are required.");
      return;
    }
    setUploading(true);
    setError("");
    setNotice("");
    try {
      const fd = new FormData();
      fd.append("label", label.trim());
      fd.append("before", beforeFile);
      fd.append("after", afterFile);
      await axios.post(`${API}/admin/gallery`, fd, { headers: authHeaders() });
      setLabel("");
      setBeforeFile(null);
      setAfterFile(null);
      e.target.reset();
      setNotice("Pair added — it's live on the site.");
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed. Try smaller images (under 15MB each).");
    } finally {
      setUploading(false);
    }
  };

  const saveLabel = async (id) => {
    try {
      await axios.patch(`${API}/admin/gallery/${id}`, { label: editLabel.trim() }, { headers: authHeaders() });
      setPairs((ps) => ps.map((p) => (p.id === id ? { ...p, label: editLabel.trim() } : p)));
      setEditId(null);
    } catch {
      setError("Label update failed. Try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this before/after pair from the site?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeaders() });
      setPairs((ps) => ps.filter((p) => p.id !== id));
      setNotice("Pair deleted.");
    } catch {
      setError("Delete failed. Try again.");
    }
  };

  return (
    <div data-testid="gallery-manager" className="min-h-screen bg-ink text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-seam">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <Link to="/admin" data-testid="gallery-back-link" className="flex items-center gap-2 font-display text-2xl font-black italic uppercase tracking-tighter">
            <ArrowLeft className="h-5 w-5 text-acid" />
            Take The <span className="text-acid">Leap</span>
            <span className="ml-2 align-middle text-[10px] font-bold not-italic tracking-[0.3em] text-zinc-500">
              Gallery Manager
            </span>
          </Link>
          <Link
            data-testid="gallery-to-quotes-link"
            to="/admin"
            className="border border-seam px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:border-acid hover:text-acid"
          >
            Quote inbox
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <form data-testid="gallery-upload-form" onSubmit={onUpload} className="border border-seam bg-coal p-6 md:p-8">
          <h2 className="font-display text-2xl font-extrabold italic uppercase tracking-tight">
            Add a before / after pair
          </h2>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            <div>
              <label htmlFor="pair-label" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Label *
              </label>
              <input
                id="pair-label"
                data-testid="gallery-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Garage Cleanout"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="pair-before" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                Before photo *
              </label>
              <input
                id="pair-before"
                data-testid="gallery-before-input"
                type="file"
                accept="image/*"
                onChange={(e) => setBeforeFile(e.target.files?.[0] || null)}
                className={`${inputClass} file:mr-3 file:border-0 file:bg-acid file:px-3 file:py-1.5 file:text-xs file:font-bold file:uppercase file:text-black`}
              />
            </div>
            <div>
              <label htmlFor="pair-after" className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500">
                After photo *
              </label>
              <input
                id="pair-after"
                data-testid="gallery-after-input"
                type="file"
                accept="image/*"
                onChange={(e) => setAfterFile(e.target.files?.[0] || null)}
                className={`${inputClass} file:mr-3 file:border-0 file:bg-acid file:px-3 file:py-1.5 file:text-xs file:font-bold file:uppercase file:text-black`}
              />
            </div>
          </div>
          {error && (
            <p data-testid="gallery-error" className="mt-5 border border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}
          {notice && (
            <p data-testid="gallery-notice" className="mt-5 border border-acid/40 bg-acid/10 px-4 py-3 text-sm text-acid">
              {notice}
            </p>
          )}
          <button
            data-testid="gallery-upload-button"
            type="submit"
            disabled={uploading}
            className="mt-6 flex items-center gap-2 bg-acid px-8 py-3.5 font-display text-lg font-extrabold italic uppercase tracking-tight text-black transition-colors duration-200 hover:bg-white disabled:opacity-60"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading…" : "Add to gallery"}
          </button>
        </form>

        <div className="mt-12">
          <h2 className="mb-6 font-display text-2xl font-extrabold italic uppercase tracking-tight">
            Live pairs {pairs && <span className="text-acid">({pairs.length})</span>}
          </h2>
          {pairs === null ? (
            <p className="py-10 text-sm text-zinc-500">Loading…</p>
          ) : pairs.length === 0 ? (
            <p data-testid="gallery-empty" className="border border-dashed border-seam py-16 text-center text-sm text-zinc-500">
              No pairs yet. Add your first before/after above.
            </p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {pairs.map((p) => (
                <div key={p.id} data-testid={`gallery-pair-${p.id}`} className="border border-seam bg-coal">
                  <div className="grid grid-cols-2">
                    <img src={fileUrl(p.before_url)} alt={`${p.label} before`} className="aspect-square w-full object-cover grayscale" loading="lazy" />
                    <img src={fileUrl(p.after_url)} alt={`${p.label} after`} className="aspect-square w-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-4">
                    {editId === p.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`gallery-edit-input-${p.id}`}
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className={inputClass}
                        />
                        <button
                          data-testid={`gallery-save-${p.id}`}
                          onClick={() => saveLabel(p.id)}
                          className="shrink-0 border border-acid p-2.5 text-acid transition-colors duration-200 hover:bg-acid hover:text-black"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate font-display text-lg font-extrabold italic uppercase tracking-tight">{p.label}</p>
                        <div className="flex shrink-0 gap-2">
                          <button
                            data-testid={`gallery-edit-${p.id}`}
                            onClick={() => { setEditId(p.id); setEditLabel(p.label); }}
                            className="border border-seam p-2.5 text-zinc-300 transition-colors duration-200 hover:border-acid hover:text-acid"
                            aria-label={`Edit label for ${p.label}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`gallery-delete-${p.id}`}
                            onClick={() => remove(p.id)}
                            className="border border-seam p-2.5 text-zinc-400 transition-colors duration-200 hover:border-red-500 hover:text-red-400"
                            aria-label={`Delete ${p.label}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
